import { governanceRecords, type GovernanceRecord } from './governance-data';

/** 08-local teaching fixtures. No actor, aggregate, authorization or audit authority. */
export type ReviewDemoStatus =
  'PENDING_REVIEW' | 'CHANGES_REQUESTED' | 'PUBLISHED' | 'REVIEW_OPINION_DEMO';
export type ReviewRuleScenario =
  'normal' | 'same-person' | 'scope' | 'permission' | 'stale' | 'repeat';
export const reviewRuleScenarios: Readonly<Record<ReviewRuleScenario, string>> = Object.freeze({
  normal: '独立人员 · 演示条件齐备',
  'same-person': '同自然人 / 多账户自审',
  scope: '跨范围审核',
  permission: '缺少审核权限',
  stale: '旧提交版本',
  repeat: '重复操作',
});
export const reviewStatusLabels: Readonly<Record<ReviewDemoStatus, string>> = Object.freeze({
  PENDING_REVIEW: '待审核',
  CHANGES_REQUESTED: '退回修改',
  PUBLISHED: '已发布（固定样例）',
  REVIEW_OPINION_DEMO: '意见已形成（本地）',
});
export interface ReviewCaseState {
  readonly demoReviewId: string;
  readonly status: ReviewDemoStatus;
  readonly opinion: string;
  readonly scenario: ReviewRuleScenario;
  readonly message: string;
  readonly completed: boolean;
  readonly history: readonly string[];
}
export type ReviewSession = Readonly<Record<string, ReviewCaseState>>;
export interface ReviewQueueFilters {
  readonly query: string;
  readonly kind: string;
  readonly status: string;
  readonly version: string;
  readonly scope: string;
  readonly since: string;
  readonly oldest: boolean;
}
export const initialReviewFilters: ReviewQueueFilters = Object.freeze({
  query: '',
  kind: 'ALL',
  status: 'ALL',
  version: 'ALL',
  scope: 'ALL',
  since: '',
  oldest: false,
});
export interface ReviewVersionSnapshot {
  readonly version: string;
  readonly purpose: string;
  readonly materials: string;
  readonly restrictions: string;
  readonly source: string;
}
export const reviewVersionFixtures: Readonly<
  Record<string, { readonly v1: ReviewVersionSnapshot; readonly v2: ReviewVersionSnapshot }>
> = Object.freeze(
  Object.fromEntries(
    governanceRecords.map((record) => [
      record.id,
      Object.freeze({
        v1: Object.freeze({
          version: 'v1',
          purpose: '基础用途描述',
          materials: '初版说明目录',
          restrictions: '尚未列示，安全限制未知',
          source: '提交方本地示例资料 v1 · 已发布固定演示样例',
        }),
        v2: Object.freeze({
          version: record.version,
          purpose: record.purpose,
          materials: '新增技术参数与风险提示目录',
          restrictions: '需核对责任方风险提示；真实性未知',
          source: `${record.kind === 'PRODUCT_MODEL' ? '厂商产品声明' : '机构本地资产描述'} ${record.version} · 独立08样例，来源未核验`,
        }),
      }),
    ]),
  ),
);
export function reviewScope(record: GovernanceRecord): string {
  return `demo-${record.kind === 'PRODUCT_MODEL' ? 'supplier' : 'agency'}-${record.id}`;
}
export function createReviewCase(record: GovernanceRecord): ReviewCaseState {
  return {
    demoReviewId: `demoReview-${record.id}`,
    status:
      record.status === '已通过'
        ? 'PUBLISHED'
        : record.status === '退回'
          ? 'CHANGES_REQUESTED'
          : 'PENDING_REVIEW',
    opinion: record.status === '退回' ? '演示反馈：请补充技术参数材料与资料来源。' : '',
    scenario: 'normal',
    message: '',
    completed: false,
    history: [],
  };
}
export function createReviewSession(): ReviewSession {
  return Object.fromEntries(
    governanceRecords.map((record) => [record.id, createReviewCase(record)]),
  );
}
export function updateReviewCase(
  session: ReviewSession,
  id: string,
  patch: Partial<Pick<ReviewCaseState, 'opinion' | 'scenario'>>,
): ReviewSession {
  const item = session[id];
  return item ? { ...session, [id]: { ...item, ...patch, message: '' } } : session;
}
export interface ReviewRuleContext {
  readonly makerAccount: string;
  readonly reviewerAccount: string;
  readonly makerNaturalPerson: string;
  readonly reviewerNaturalPerson: string;
  readonly subjectScope: string;
  readonly reviewerScope: string;
  readonly permission: boolean;
  readonly submittedVersion: string;
  readonly viewedVersion: string;
  readonly repeat: boolean;
}
export function reviewRuleContext(
  record: GovernanceRecord,
  item: ReviewCaseState,
): ReviewRuleContext {
  return {
    makerAccount: 'demo-account-maker',
    reviewerAccount: 'demo-account-reviewer',
    makerNaturalPerson: 'demo-person-maker',
    reviewerNaturalPerson:
      item.scenario === 'same-person' ? 'demo-person-maker' : 'demo-person-reviewer',
    subjectScope: reviewScope(record),
    reviewerScope: item.scenario === 'scope' ? 'demo-outside-scope' : reviewScope(record),
    permission: item.scenario !== 'permission',
    submittedVersion: item.scenario === 'stale' ? 'v3' : record.version,
    viewedVersion: record.version,
    repeat: item.completed || item.scenario === 'repeat',
  };
}
export function reviewRuleRefusal(context: ReviewRuleContext): string | null {
  if (context.makerNaturalPerson === context.reviewerNaturalPerson)
    return '禁止自审：账户不同，但 naturalPersonKey 为同一自然人。仅规则演示，待服务端可信自然人核验。';
  if (context.subjectScope !== context.reviewerScope)
    return '跨范围审核拒绝：供应商或机构 Scope 不匹配。仅演示，真实范围需服务端核验。';
  if (!context.permission) return '缺少审核权限：拒绝形成意见。仅演示，真实 Permission 尚未接入。';
  if (context.submittedVersion !== context.viewedVersion)
    return '旧提交版本冲突：当前资料版本已变化，请重新核对。仅演示，未实现服务端并发控制。';
  if (context.repeat) return '重复操作拒绝：该演示版本已处理。仅本地规则，未实现服务端幂等性。';
  return null;
}
export function validateReviewOpinion(opinion: string): string | null {
  if (!opinion.trim()) return '退回原因 / 审核意见必填，请说明需修改或赞同的依据。';
  if (opinion.length > 500) return '审核意见不能超过500字。';
  if (
    /[<>]/u.test(opinion) ||
    [...opinion].some(
      (character) => character.charCodeAt(0) < 32 && !['\n', '\r', '\t'].includes(character),
    )
  )
    return '审核意见只接受纯文本，请移除HTML标记或控制字符。';
  return null;
}
export function applyReviewDemoAction(
  session: ReviewSession,
  id: string,
  action: 'return' | 'agree',
): ReviewSession {
  const item = session[id];
  const record = governanceRecords.find((value) => value.id === id);
  if (!item || !record) return session;
  const reason =
    reviewRuleRefusal(reviewRuleContext(record, item)) ??
    (item.status === 'PUBLISHED'
      ? '已发布固定样例只读，不能重新审核或覆盖版本。'
      : item.status === 'CHANGES_REQUESTED'
        ? '当前已退回；重新演示请先恢复本条静态样例，不代表收到修订。'
        : validateReviewOpinion(item.opinion));
  if (reason) return { ...session, [id]: { ...item, message: reason } };
  const message =
    action === 'return'
      ? '模拟退回已形成：CHANGES_REQUESTED · DEMO。未向提交人发送；仅08当前会话变化。'
      : `REVIEW_OPINION_DEMO · 已模拟形成赞同审核意见。等待${record.kind === 'PRODUCT_MODEL' ? '06' : '05'} Owner发布 → 发布结果读回待核对。未提交决定、未发布。`;
  return {
    ...session,
    [id]: {
      ...item,
      status: action === 'return' ? 'CHANGES_REQUESTED' : 'REVIEW_OPINION_DEMO',
      completed: true,
      message,
      history: [...item.history, `${message} 意见：${item.opinion.trim()}`],
    },
  };
}
export function resetReviewDemoCase(session: ReviewSession, id: string): ReviewSession {
  const record = governanceRecords.find((value) => value.id === id);
  return record ? { ...session, [id]: createReviewCase(record) } : session;
}
export function noteReviewReadback(session: ReviewSession, id: string): ReviewSession {
  const item = session[id];
  return item
    ? {
        ...session,
        [id]: {
          ...item,
          message:
            '正式发布结果读回不可用：未连接05/06 Owner发布合同，无PublishedRef或确认结果。保持等待，不推断发布成功。',
        },
      }
    : session;
}
export function filterReviewQueue(
  filters: ReviewQueueFilters,
  session: ReviewSession,
): readonly GovernanceRecord[] {
  const result = governanceRecords.filter(
    (record) =>
      (filters.kind === 'ALL' || record.kind === filters.kind) &&
      (filters.status === 'ALL' || session[record.id]?.status === filters.status) &&
      (filters.version === 'ALL' || record.version === filters.version) &&
      (filters.scope === 'ALL' || reviewScope(record) === filters.scope) &&
      (!filters.since || record.submitted.slice(0, 10) >= filters.since) &&
      `${record.name} ${record.model} ${record.submitter}`
        .toLowerCase()
        .includes(filters.query.trim().toLowerCase()),
  );
  return filters.oldest ? [...result].reverse() : result;
}
